import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  BarChart2, CheckCircle2, ChevronRight, ChevronLeft,
  ExternalLink, AlertCircle, Globe, Zap,
} from "lucide-react";
import { ConorbitLoader } from "@/components/ui/ConorbitLoader";

// ─── Provider definitions ────────────────────────────────────────────────────

const PROVIDERS = [
  {
    value: 'ga4',
    label: 'Google Analytics 4',
    logo: '🔵',
    tagline: 'The most popular analytics platform, made by Google.',
    steps: [
      {
        title: 'Create a Google Analytics account',
        body: 'If you do not have Google Analytics yet:',
        bullets: [
          'Go to analytics.google.com and sign in with your Google account.',
          'Click "Start measuring" and follow the setup wizard.',
          'Choose "Web" as the platform and enter your website URL.',
          'Once done you will have a GA4 property.',
        ],
        note: 'Already have GA4? Skip straight to Step 2.',
        link: { label: 'Open Google Analytics', url: 'https://analytics.google.com' },
      },
      {
        title: 'Find your Property ID',
        body: 'You need a number called a "Property ID" to connect:',
        bullets: [
          'Inside Google Analytics, click the gear icon (⚙) at the bottom-left — this opens Admin.',
          'Under the "Property" column, click "Property Settings".',
          'At the top you will see a number like 123456789 — that is your Property ID.',
          'Copy and keep it handy.',
        ],
        field: { key: 'property_id', label: 'Paste your Property ID here', placeholder: '123456789', type: 'text' },
      },
      {
        title: 'Create a Service Account in Google Cloud',
        body: 'This gives CONORBIT read-only access to your analytics data. It sounds technical but takes just a few minutes:',
        bullets: [
          'Go to console.cloud.google.com and sign in.',
          'Select the same Google project that your Analytics belongs to (or create a new one named "CONORBIT Analytics").',
          'In the left sidebar, go to "IAM & Admin" → "Service Accounts".',
          'Click "+ Create Service Account". Name it "conorbit-analytics" and click "Done".',
          'Click on the service account you just created.',
          'Go to the "Keys" tab → "Add Key" → "Create new key" → choose "JSON" → click "Create".',
          'A JSON file will download to your computer. Keep it safe — you will paste it in the next step.',
        ],
        link: { label: 'Open Google Cloud Console', url: 'https://console.cloud.google.com/iam-admin/serviceaccounts' },
      },
      {
        title: 'Enable the Analytics Data API',
        body: 'We need to turn on the API that lets us read your data:',
        bullets: [
          'In Google Cloud Console, click the search bar at the top.',
          'Search for "Google Analytics Data API" and click it.',
          'Click the blue "Enable" button.',
          'Wait a few seconds for it to activate.',
        ],
        link: { label: 'Enable the API directly', url: 'https://console.cloud.google.com/apis/library/analyticsdata.googleapis.com' },
      },
      {
        title: 'Grant the service account access to your GA4 property',
        body: 'Go back to Google Analytics and add the service account as a viewer:',
        bullets: [
          'In Google Analytics, click the gear icon (⚙) → Admin.',
          'Under "Property", click "Property Access Management".',
          'Click the blue "+" button in the top-right.',
          'Enter the service account email (it looks like conorbit-analytics@your-project.iam.gserviceaccount.com — find it in your JSON file under "client_email").',
          'Set the role to "Viewer" and click "Add".',
        ],
      },
      {
        title: 'Paste your Service Account JSON key',
        body: 'Open the JSON file you downloaded and paste its entire contents below:',
        bullets: [
          'Open the downloaded JSON file with any text editor (Notepad, TextEdit, etc.).',
          'Select all the text (Ctrl+A or Cmd+A) and copy it.',
          'Paste it in the box below.',
        ],
        field: { key: 'service_account_key', label: 'Service Account JSON Key', placeholder: '{\n  "type": "service_account",\n  "project_id": "...",\n  "client_email": "...",\n  ...\n}', type: 'textarea' },
      },
    ],
  },
  {
    value: 'plausible',
    label: 'Plausible Analytics',
    logo: '🟣',
    tagline: 'A privacy-friendly, simple analytics tool. Great alternative to Google.',
    steps: [
      {
        title: 'Create a Plausible account',
        body: 'If you are new to Plausible:',
        bullets: [
          'Go to plausible.io and click "Start free trial".',
          'Sign up and add your website domain (e.g. conorbit.com).',
          'Follow their guide to add the tracking snippet to your website.',
        ],
        note: 'Already on Plausible? Skip to Step 2.',
        link: { label: 'Open Plausible.io', url: 'https://plausible.io' },
      },
      {
        title: 'Find your Site ID',
        body: 'Your Site ID is simply your domain name:',
        bullets: [
          'Log in to Plausible.',
          'You will see your site listed on the dashboard — the domain name shown (e.g. yoursite.com) is your Site ID.',
          'Copy it exactly as shown, without "https://".',
        ],
        field: { key: 'site_id', label: 'Your domain / Site ID', placeholder: 'yoursite.com', type: 'text' },
      },
      {
        title: 'Create an API Key',
        body: 'An API key lets CONORBIT read your stats:',
        bullets: [
          'In Plausible, click your profile icon (top-right) → "Account Settings".',
          'Scroll down to the "API Keys" section.',
          'Click "New API Key", give it a name like "CONORBIT", and click "Create API Key".',
          'Copy the key that appears — you will only see it once.',
        ],
        field: { key: 'api_key', label: 'Paste your Plausible API Key', placeholder: 'plausible_api_...', type: 'text' },
      },
      {
        title: 'Self-hosted Plausible? (optional)',
        body: 'Only fill this in if you host Plausible yourself on your own server:',
        bullets: [
          'Enter the URL where your Plausible instance is running.',
          'Leave blank if you use the official Plausible Cloud (plausible.io).',
        ],
        field: { key: 'base_url', label: 'Custom Plausible URL (optional)', placeholder: 'https://analytics.yoursite.com', type: 'text', optional: true },
      },
    ],
  },
  {
    value: 'matomo',
    label: 'Matomo',
    logo: '🟠',
    tagline: 'An open-source analytics platform. Full control of your data.',
    steps: [
      {
        title: 'Find your Matomo URL',
        body: 'This is the web address where your Matomo is installed:',
        bullets: [
          'Log in to your Matomo dashboard.',
          'The URL in your browser address bar is your Matomo URL (e.g. https://analytics.yoursite.com).',
          'Copy it without any page path — just up to the domain.',
        ],
        field: { key: 'site_url', label: 'Your Matomo URL', placeholder: 'https://analytics.yoursite.com', type: 'text' },
      },
      {
        title: 'Find your Site ID',
        body: 'Each website tracked in Matomo has a Site ID:',
        bullets: [
          'In Matomo, click the gear icon (⚙) top-right to open Administration.',
          'Go to "Websites" → "Manage".',
          'Find your website in the list — the number in the leftmost column is your Site ID.',
        ],
        field: { key: 'site_id', label: 'Site ID (a number)', placeholder: '1', type: 'text' },
      },
      {
        title: 'Create an Auth Token',
        body: 'An auth token lets CONORBIT read your analytics without your password:',
        bullets: [
          'In Matomo, click your username (top-right) → "Personal Settings".',
          'Scroll to the "Security" section.',
          'Under "Auth tokens", click "Create new token".',
          'Enter a description like "CONORBIT" and click "Confirm and generate the token".',
          'Copy the long string of characters shown.',
        ],
        field: { key: 'auth_token', label: 'Paste your Auth Token', placeholder: 'abc123def456...', type: 'text' },
      },
    ],
  },
  {
    value: 'cloudflare',
    label: 'Cloudflare Analytics',
    logo: '🟡',
    tagline: 'Built into Cloudflare. Works automatically if your site uses Cloudflare.',
    steps: [
      {
        title: 'Make sure your site uses Cloudflare',
        body: 'Cloudflare Analytics only works if your website\'s traffic goes through Cloudflare:',
        bullets: [
          'Log in to dash.cloudflare.com.',
          'You should see your domain listed. If not, you need to add it first.',
          'Make sure the orange cloud icon is shown next to your domain — this means Cloudflare is active.',
        ],
        link: { label: 'Open Cloudflare Dashboard', url: 'https://dash.cloudflare.com' },
      },
      {
        title: 'Find your Zone ID',
        body: 'A Zone ID uniquely identifies your domain in Cloudflare:',
        bullets: [
          'In the Cloudflare dashboard, click on your domain.',
          'Scroll down on the right-hand side of the Overview page.',
          'Under "API" you will see "Zone ID" — it is a long string of letters and numbers.',
          'Click "Copy" next to it.',
        ],
        field: { key: 'zone_id', label: 'Paste your Zone ID', placeholder: '023e105f4ecef8ad9ca31a8372d0c353', type: 'text' },
      },
      {
        title: 'Create an API Token',
        body: 'An API token gives CONORBIT read-only access to your analytics:',
        bullets: [
          'In Cloudflare, click your profile picture (top-right) → "My Profile".',
          'Click "API Tokens" in the left menu.',
          'Click "Create Token".',
          'Click "Use template" next to "Read analytics".',
          'Under "Zone Resources" select your domain from the dropdown.',
          'Click "Continue to summary" → "Create Token".',
          'Copy the token shown — you will only see it once.',
        ],
        field: { key: 'api_token', label: 'Paste your API Token', placeholder: 'your-cloudflare-api-token...', type: 'text' },
      },
    ],
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

export function WebsiteAnalyticsSettings() {
  const { orgId } = useOrgPermissions();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [fields, setFields] = useState<Record<string, string>>({});

  const { data: existingRaw, isLoading } = useQuery({
    queryKey: ['org_analytics_config', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from('org_analytics_config' as any)
        .select('*')
        .eq('org_id', orgId!)
        .maybeSingle() as any);
      return data as { provider: string; config: Record<string, string>; is_active: boolean } | null;
    },
    onSuccess: (data: any) => {
      if (data && !selectedProvider) {
        setSelectedProvider(data.provider);
        const providerDef = PROVIDERS.find(p => p.value === data.provider);
        const safe: Record<string, string> = {};
        providerDef?.steps.forEach(s => { if (s.field) safe[s.field.key] = data.config?.[s.field.key] ?? ''; });
        setFields(safe);
      }
    },
  } as any);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProvider || !orgId) throw new Error('Select a provider first');
      const providerDef = PROVIDERS.find(p => p.value === selectedProvider)!;
      const config: Record<string, string> = {};
      providerDef.steps.forEach(s => { if (s.field && fields[s.field.key]) config[s.field.key] = fields[s.field.key]; });
      // Validate required fields
      for (const s of providerDef.steps) {
        if (s.field && !(s.field as any).optional && !config[s.field.key]?.trim()) {
          throw new Error(`"${s.field.label}" is required`);
        }
      }
      await (supabase
        .from('org_analytics_config' as any)
        .upsert({ org_id: orgId, provider: selectedProvider, config, is_active: true }, { onConflict: 'org_id' }) as any);
    },
    onSuccess: () => {
      toast({ title: 'Analytics provider connected!' });
      queryClient.invalidateQueries({ queryKey: ['org_analytics_config', orgId] });
      queryClient.invalidateQueries({ queryKey: ['org_analytics_config_exists'] });
      queryClient.invalidateQueries({ queryKey: ['website_analytics'] });
    },
    onError: (err: Error) => toast({ title: 'Save failed', description: err.message, variant: 'destructive' }),
  });

  const disableMutation = useMutation({
    mutationFn: async () => {
      await (supabase.from('org_analytics_config' as any).update({ is_active: false }).eq('org_id', orgId!) as any);
    },
    onSuccess: () => {
      toast({ title: 'Analytics disconnected' });
      queryClient.invalidateQueries({ queryKey: ['org_analytics_config', orgId] });
      queryClient.invalidateQueries({ queryKey: ['org_analytics_config_exists'] });
      queryClient.invalidateQueries({ queryKey: ['website_analytics'] });
      setSelectedProvider(null);
      setFields({});
      setStep(0);
    },
  });

  const existing = existingRaw as { provider: string; config: Record<string, string>; is_active: boolean } | null;

  const providerDef = PROVIDERS.find(p => p.value === selectedProvider);
  const currentStep = providerDef?.steps[step];
  const totalSteps = providerDef?.steps.length ?? 0;
  const isLastStep = step === totalSteps - 1;

  if (isLoading) return <ConorbitLoader variant="inline" />;

  // ── Provider picker ──────────────────────────────────────────────────────
  if (!selectedProvider) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="font-semibold text-sm">Connect Website Analytics</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Connect your website analytics to display traffic data on the dashboard.
            Choose the tool your website already uses — we will walk you through it step by step.
          </p>
        </div>

        {existing?.is_active && (
          <div className="flex items-center gap-3 p-3 rounded-lg border border-green-200 bg-green-50">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-800">
                {PROVIDERS.find(p => p.value === existing.provider)?.label} is connected
              </p>
              <p className="text-xs text-green-700">Your website analytics are live on the dashboard.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setSelectedProvider(existing.provider)}>
              Edit
            </Button>
            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => disableMutation.mutate()} disabled={disableMutation.isPending}>
              Disconnect
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PROVIDERS.map(p => (
            <button
              key={p.value}
              onClick={() => { setSelectedProvider(p.value); setStep(0); }}
              className="text-left p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors group"
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">{p.logo}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{p.label}</span>
                    {existing?.provider === p.value && existing.is_active && (
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{p.tagline}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0 mt-0.5" />
              </div>
            </button>
          ))}
        </div>

        <p className="text-xs text-muted-foreground">
          Not sure which one to use?{' '}
          <span className="font-medium">Plausible</span> is the easiest to set up.{' '}
          <span className="font-medium">Google Analytics 4</span> is the most common.
        </p>
      </div>
    );
  }

  // ── Step-by-step wizard ──────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => { setSelectedProvider(null); setStep(0); }} className="text-muted-foreground hover:text-foreground">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-lg">{providerDef!.logo}</span>
            <h3 className="font-semibold text-sm">{providerDef!.label}</h3>
            {existing?.provider === selectedProvider && existing.is_active && (
              <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50 gap-1 text-xs">
                <CheckCircle2 className="w-3 h-3" /> Connected
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{providerDef!.tagline}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Step {step + 1} of {totalSteps}</span>
          <span>{Math.round(((step + 1) / totalSteps) * 100)}% complete</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-[#006B3C] rounded-full transition-all duration-300"
            style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Step dots */}
      <div className="flex gap-1.5">
        {providerDef!.steps.map((_, i) => (
          <button
            key={i}
            onClick={() => setStep(i)}
            className={`h-1.5 rounded-full transition-all ${i === step ? 'bg-[#006B3C] w-6' : i < step ? 'bg-[#006B3C]/40 w-3' : 'bg-muted w-3'}`}
          />
        ))}
      </div>

      {/* Step content */}
      {currentStep && (
        <div className="border rounded-lg p-5 space-y-4 bg-muted/20">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 rounded-full bg-[#006B3C] text-white text-xs flex items-center justify-center font-bold shrink-0">
                {step + 1}
              </div>
              <h4 className="font-semibold text-sm">{currentStep.title}</h4>
            </div>
            <p className="text-sm text-muted-foreground ml-8">{currentStep.body}</p>
          </div>

          {currentStep.bullets && (
            <ol className="ml-8 space-y-2">
              {currentStep.bullets.map((b, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-muted text-muted-foreground text-xs flex items-center justify-center font-medium mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-foreground/80 leading-relaxed">{b}</span>
                </li>
              ))}
            </ol>
          )}

          {currentStep.note && (
            <div className="ml-8 flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">{currentStep.note}</p>
            </div>
          )}

          {currentStep.link && (
            <div className="ml-8">
              <a
                href={currentStep.link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline font-medium"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {currentStep.link.label}
              </a>
            </div>
          )}

          {currentStep.field && (
            <div className="ml-8 space-y-1.5">
              <Label htmlFor={`analytics-${currentStep.field.key}`} className="text-sm font-medium">
                {currentStep.field.label}
                {(currentStep.field as any).optional && (
                  <span className="ml-1 text-xs font-normal text-muted-foreground">(optional)</span>
                )}
              </Label>
              {currentStep.field.type === 'textarea' ? (
                <Textarea
                  id={`analytics-${currentStep.field.key}`}
                  placeholder={currentStep.field.placeholder}
                  value={fields[currentStep.field.key] ?? ''}
                  onChange={e => setFields(prev => ({ ...prev, [currentStep.field!.key]: e.target.value }))}
                  rows={7}
                  className="font-mono text-xs"
                />
              ) : (
                <Input
                  id={`analytics-${currentStep.field.key}`}
                  placeholder={currentStep.field.placeholder}
                  value={fields[currentStep.field.key] ?? ''}
                  onChange={e => setFields(prev => ({ ...prev, [currentStep.field!.key]: e.target.value }))}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setStep(s => Math.max(0, s - 1))}
          disabled={step === 0}
        >
          <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Previous
        </Button>

        {isLastStep ? (
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="gap-2"
          >
            <Zap className="w-3.5 h-3.5" />
            {saveMutation.isPending ? 'Connecting…' : 'Connect & Activate'}
          </Button>
        ) : (
          <Button size="sm" onClick={() => setStep(s => s + 1)}>
            Next step <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        )}
      </div>

      {/* All steps overview */}
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer hover:text-foreground select-none">View all steps</summary>
        <ol className="mt-2 space-y-1 ml-4">
          {providerDef!.steps.map((s, i) => (
            <li key={i} className={`flex items-center gap-2 ${i < step ? 'text-green-600' : i === step ? 'text-foreground font-medium' : ''}`}>
              {i < step ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-current inline-block" />}
              {s.title}
            </li>
          ))}
        </ol>
      </details>
    </div>
  );
}
